import { useEffect, useState } from "react";
import { resolveCommittedNumber } from "@/features/settings/ui/numberDraft";
import { Input, type InputProps } from "@/shared/ui/form";

export interface NumberSettingInputProps
  extends Omit<
    InputProps,
    "value" | "onChange" | "type" | "defaultValue" | "min" | "max"
  > {
  /** 現在確定している数値(ストア値)。 */
  value: number;
  /** blur / Enter の確定時に呼ぶ。渡す値は [min, max] へ丸め済み。 */
  onCommit: (value: number) => void;
  /** 下限。省略時は下限なし。 */
  min?: number;
  /** 上限。省略時は上限なし。 */
  max?: number;
}

/**
 * 数値設定用の入力欄。
 *
 * type="number" の onChange で毎キー clamp 済み setter を呼ぶと、目的の値の
 * 途中経過(例: "1000" の1桁目 "1")が範囲外になった瞬間に下限へ丸められ、
 * 目的の値をキーボードで入力できない。これを避けるため、編集中はローカルの
 * draft 文字列で自由入力を許し、blur と Enter の確定時にのみ [min, max] へ
 * 丸めて onCommit する。ストア値(value)が外部から変わった場合は、編集中で
 * なければ draft を同期する(全データクリア等の外部更新に追従する)。
 */
export const NumberSettingInput: React.FunctionComponent<
  NumberSettingInputProps
> = ({
  value,
  onCommit,
  min,
  max,
  onFocus,
  onBlur,
  onKeyDown,
  ...rest
}: NumberSettingInputProps) => {
  const [draft, setDraft] = useState<string>(String(value));
  const [isEditing, setIsEditing] = useState(false);

  // 編集中でなければストア値の変化へ追従する。編集中はユーザーの入力途中を上書きしない。
  useEffect((): void => {
    if (!isEditing) setDraft(String(value));
  }, [value, isEditing]);

  const commit = (): number => {
    const next = resolveCommittedNumber(draft, value, min, max);
    onCommit(next);
    return next;
  };

  return (
    <Input
      {...rest}
      type="number"
      min={min}
      max={max}
      value={draft}
      onFocus={(event: React.FocusEvent<HTMLInputElement>): void => {
        setIsEditing(true);
        onFocus?.(event);
      }}
      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
        setDraft(event.target.value);
      }}
      onBlur={(event: React.FocusEvent<HTMLInputElement>): void => {
        setIsEditing(false);
        commit();
        onBlur?.(event);
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === "Enter") {
          // 確定中はまだ blur していないため、丸めた値を draft へ即時反映する。
          setDraft(String(commit()));
        }
        onKeyDown?.(event);
      }}
    />
  );
};

NumberSettingInput.displayName = "NumberSettingInput";
